<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { getHostRouter } from '@Src/mfe.config';
import { trackPaymentMethodSelected, trackRequestStarted } from '@Analytics/request.mixpanel';
import {
    WIRE_FEE_ESTIMATE_USD,
    PaymentMethodChoice,
} from '@Constants/paymentMethod.constants';
import { isEmdSendType } from '@Enums/sendType.enum';
import { FlowLayout, useDateFormatter } from '@Shared';
import { useFlowSteps } from '@Use/useFlowSteps';
import useTaskNavTitle from '@Use/useTaskNavTitle';
import { isSameDayWindowMissed } from '@Enums/ctcEligibilityStatus.enum';

const { formatLocalDateTime } = useDateFormatter();

const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestDetailsStore = useRequestDetailsStore();
const { advanceFlow, goBackInFlow } = useFlowNavigation();
const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const { taskNavTitle } = useTaskNavTitle();

const isLoading = ref(true);
const isNavigating = ref(false);
const isPayingNow = ref(false);

const requestDetails = computed(() => requestDetailsStore.requestDetails);
const isEligibleForDigital = computed(() => requestDetails.value?.isEligibleForDigital ?? false);

// PAY-3511: branch banner copy when same-day window is missed.
// Note: the banner's VISIBILITY is intentionally governed solely by !isEligibleForDigital and must not be widened by ctcEligibilityStatus.
// Do not change the v-if guard that renders the banner — a prior attempt widened it and had to be reverted.
const bannerCopy = computed(() => {
    if (isSameDayWindowMissed(requestDetails.value?.ctcEligibilityStatus)) {
        return 'The same-day payment window for this closing has passed — digital payments must be submitted at least 4 hours before closing. You can still pay by wire.';
    }
    return 'The digital payment window for this closing has passed. Wire instructions are available as an alternative.';
});

const paymentTypeLabel = computed(() =>
    isEmdSendType(requestDetails.value?.sendType) ? 'Earnest Money Deposit' : 'Cash to Close',
);

// "Guaranteed by closing" only applies to Cash to Close. EMD isn't tied to closing,
// and the relative arrival label already sets the expectation, so no subtext is shown.
const guaranteedByCopy = computed(() =>
    isEmdSendType(requestDetails.value?.sendType) ? '' : 'Guaranteed by closing',
);
const guaranteedArrival = computed(() =>
    formatLocalDateTime(requestDetails.value?.guaranteedArrivalUtc),
);

const arrivesLabel = computed(() => {
    // EMD isn't tied to a closing date, so show a relative window rather than a
    // specific arrival date.
    if (isEmdSendType(requestDetails.value?.sendType)) {
        return 'Arrives within 1-2 business days';
    }
    return guaranteedArrival.value
        ? `Arrives by ${guaranteedArrival.value.date}`
        : 'Guaranteed arrival';
});

const payNow = () => {
    if (isNavigating.value) return;

    isNavigating.value = true;
    isPayingNow.value = true;

    trackPaymentMethodSelected(PaymentMethodChoice.Digital, isEligibleForDigital.value);

    const hostRouter = getHostRouter();
    const companySlug = hostRouter.currentRoute.value.params.companySlug;
    hostRouter.push(`/${companySlug}/payments/startup`);
};

const viewWireInstructions = () => {
    if (isNavigating.value) return;

    isNavigating.value = true;

    trackPaymentMethodSelected(PaymentMethodChoice.Wire, isEligibleForDigital.value);
    trackRequestStarted(isEligibleForDigital.value);
    advanceFlow();
};

onMounted(async () => {
    isLoading.value = true;
    await requestDetailsStore.fetchRequestDetails();
    isLoading.value = false;
});
</script>

<template>
    <flow-layout
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        show-back-button
        @back-click="goBackInFlow"
    >
        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <template v-if="isLoading">
            <app-page-loader />
        </template>

        <template v-else>
            <div class="payment-methods">
                <h1 class="payment-methods__title">Choose how to pay</h1>
                <p class="payment-methods__subtitle">
                    Review your options for paying your {{ paymentTypeLabel }}.
                </p>

                <!-- Ineligibility banner -->
                <div
                    v-if="!isEligibleForDigital"
                    class="payment-methods__warning"
                    role="status"
                >
                    <app-icon
                        icon="cd-ic-alert-circle"
                        class="payment-methods__warning-icon"
                    />
                    <p class="payment-methods__warning-text">
                        {{ bannerCopy }}
                    </p>
                </div>

                <!-- Digital card -->
                <div
                    class="payment-methods__card payment-methods__card--digital"
                    :class="{
                        'payment-methods__card--disabled': !isEligibleForDigital,
                    }"
                    :aria-disabled="!isEligibleForDigital"
                    data-id="payment-methods-digital-card"
                >
                    <template v-if="isEligibleForDigital">
                        <div class="payment-methods__card-header">
                            <p class="payment-methods__card-title">Pay from your bank account</p>
                            <p class="payment-methods__card-tagline">
                                Takes less than 5 minutes.
                            </p>
                        </div>

                        <div class="payment-methods__card-body">
                            <!-- The fee is not shown here: it is disclosed on the payment review screen,
                                 where buyers see it at the point of paying (fee-at-the-end pricing). -->
                            <div class="payment-methods__value-props">
                                <div class="payment-methods__value-prop">
                                    <app-icon icon="cd-ic-calendar" class="payment-methods__value-prop-icon" />
                                    <div class="payment-methods__value-prop-text">
                                        <p class="payment-methods__value-prop-label">{{ arrivesLabel }}</p>
                                        <p v-if="guaranteedByCopy" class="payment-methods__value-prop-desc">
                                            {{ guaranteedByCopy }}
                                        </p>
                                    </div>
                                </div>

                                <div class="payment-methods__value-prop">
                                    <app-icon icon="cd-ic-money-shield" class="payment-methods__value-prop-icon" />
                                    <div class="payment-methods__value-prop-text">
                                        <p class="payment-methods__value-prop-label">Fully insured</p>
                                        <p class="payment-methods__value-prop-desc">
                                            Every dollar is protected
                                        </p>
                                    </div>
                                </div>

                                <div class="payment-methods__value-prop">
                                    <app-icon icon="cd-ic-locked" class="payment-methods__value-prop-icon" />
                                    <div class="payment-methods__value-prop-text">
                                        <p class="payment-methods__value-prop-label">Bank-level encryption</p>
                                        <p class="payment-methods__value-prop-desc">
                                            Your login details are never shared
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <app-button
                                expanded
                                size="large"
                                :is-loading="isPayingNow"
                                :is-disabled="isNavigating"
                                data-id="payment-methods-digital-pay-now"
                                @onClick="payNow"
                            >
                                Pay Now
                            </app-button>
                        </div>
                    </template>

                    <template v-else>
                        <div class="payment-methods__card-disabled-header">
                            <div class="payment-methods__disabled-title-row">
                                <p class="payment-methods__card-title payment-methods__card-title--disabled">
                                    Pay digitally
                                </p>
                                <span class="payment-methods__badge payment-methods__badge--grey">Unavailable</span>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- PAY-3719: when digital is available, wire is a secondary text option, not a peer card.
                     De-emphasize, do not discredit — no warning iconography or error styling here. -->
                <div
                    v-if="isEligibleForDigital"
                    class="payment-methods__wire-alt"
                    data-id="payment-methods-wire-alt"
                >
                    <p class="payment-methods__wire-alt-title">Visit your bank instead</p>
                    <p class="payment-methods__wire-alt-desc">
                        You'll take verified instructions to your bank and enter the details yourself.
                        Your bank will charge its own wire fee.
                    </p>
                    <button
                        type="button"
                        class="payment-methods__wire-link payment-methods__wire-link--subtle"
                        data-id="payment-methods-wire-link"
                        :aria-disabled="isNavigating"
                        @click="viewWireInstructions"
                    >
                        Get wire instructions
                    </button>
                </div>

                <!-- Wire card: shown when digital is unavailable (unchanged by PAY-3719) -->
                <div
                    v-else
                    class="payment-methods__card payment-methods__card--wire"
                    data-id="payment-methods-wire-card"
                >
                    <div class="payment-methods__wire-header">
                        <app-icon icon="cd-ic-bank" class="payment-methods__wire-header-icon" />
                        <p class="payment-methods__card-title">Download wire instructions</p>
                    </div>
                    <p class="payment-methods__wire-subtitle">Traditional bank wire transfer</p>
                    <p class="payment-methods__wire-desc">
                        You'll download verified wire instructions. Enter the details through your bank and verify everything manually.
                    </p>
                    <p class="payment-methods__wire-fee">
                        <span class="payment-methods__wire-fee-amount">${{ WIRE_FEE_ESTIMATE_USD }}</span>
                        estimated fee paid through your bank
                    </p>

                    <div class="payment-methods__wire-insurance">
                        + Optional wire insurance
                    </div>

                    <div class="payment-methods__wire-link-wrapper">
                        <button
                            type="button"
                            class="payment-methods__wire-link"
                            data-id="payment-methods-wire-link"
                            :aria-disabled="isNavigating"
                            @click="viewWireInstructions"
                        >
                            View wire instructions
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </flow-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

:deep(.flow-layout-body .left) {
    display: none !important;
}

.payment-methods {
    display: flex;
    flex-direction: column;

    &__title {
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1.3;
        color: var(--ch-primary-dark, $dark-blue);
        margin: 0 0 0.75rem;
    }

    &__subtitle {
        font-size: 0.9375rem;
        font-weight: 400;
        line-height: 1.6;
        color: $black-med;
        margin: 0 0 1.5rem;
    }

    &__warning {
        display: flex;
        align-items: flex-start;
        gap: 0.625rem;
        padding: 0.875rem 1rem;
        margin-bottom: 1rem;
        background: #FEF3C7;
        border: 1px solid #FDE68A;
        border-radius: 0.5rem;
    }

    &__warning-icon {
        flex-shrink: 0;
        margin-top: 0.0625rem;
        font-size: 1.25rem;
        color: #D97706;
    }

    &__warning-text {
        font-size: 0.8125rem;
        line-height: 1.5;
        color: #92400E;
        margin: 0;
    }

    &__card {
        border-radius: 0.75rem;
        border: 2px solid $gray-lightest;
        background: $white;
        margin-bottom: 1rem;
        overflow: hidden;

        &--disabled {
            opacity: 0.45;
            background: $gray-lightest;
            border-color: $gray-lightest;
        }
    }

    &__card--digital {
        border-color: color-mix(in srgb, var(--ch-primary, $blue) 30%, transparent);
    }

    &__card--wire {
        padding: 1.25rem;
        border-style: dashed;
        background: rgba($gray-lightest, 0.5);
    }

    &__card-header {
        background: var(--ch-primary-dark, $dark-blue);
        padding: 1.25rem 1.25rem 1rem;
    }

    &__badge {
        display: inline-block;
        padding: 0.25rem 0.625rem;
        border-radius: 1rem;
        font-size: $xs-small-text;
        font-weight: 700;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        margin-bottom: 0.625rem;

        &--grey {
            background: $gray-lightest;
            color: $black-low;
            margin-bottom: 0;
        }
    }

    &__card-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: $white;
        line-height: 1.3;
        margin: 0 0 0.25rem;

        &--disabled {
            color: $black-high;
            font-size: 1rem;
            margin: 0;
        }
    }

    &__wire-header {
        display: flex;
        align-items: center;
        gap: 0.625rem;
        margin-bottom: 0.5rem;
    }

    &__wire-header-icon {
        flex-shrink: 0;
        font-size: 1.25rem;
        color: var(--ch-primary-dark, $dark-blue);
    }

    .payment-methods__card--wire .payment-methods__card-title {
        color: var(--ch-primary-dark, $dark-blue);
        font-size: 1.125rem;
        margin: 0;
    }

    &__wire-subtitle {
        font-size: 0.875rem;
        color: $black-med;
        margin: 0 0 0.5rem;
        font-weight: 500;
    }

    &__card-tagline {
        font-size: 0.875rem;
        color: rgba(255, 255, 255, 0.7);
        margin: 0;
    }

    &__card-body {
        padding: 1rem 1.25rem 1.25rem;
    }

    &__value-props {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    &__value-prop {
        display: flex;
        align-items: flex-start;
        gap: 0.625rem;
    }

    &__value-prop-icon {
        flex-shrink: 0;
        margin-top: 0.125rem;
        font-size: 1.125rem;
        color: var(--ch-primary, $blue);
    }

    &__value-prop-label {
        font-size: 0.875rem;
        font-weight: 700;
        color: $black-high;
        margin: 0;
    }

    &__value-prop-desc {
        font-size: 0.75rem;
        color: $black-med;
        margin: 0.125rem 0 0;
    }

    &__card-disabled-header {
        padding: 1rem 1.25rem;
    }

    &__disabled-title-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }


    &__wire-fee {
        font-size: 0.8125rem;
        color: $black-low;
        margin: 0 0 0.5rem;
    }

    &__wire-fee-amount {
        font-size: 1.375rem;
        font-weight: 700;
        color: $black-med;
        margin-right: 0.5rem;
    }

    &__wire-desc {
        font-size: 0.8125rem;
        line-height: 1.5;
        color: $black-med;
        margin: 0 0 0.75rem;
    }

    // PAY-3719: set apart from the digital card so wire reads as an afterthought, and
    // aligned to the same left edge as the title and card.
    &__wire-alt {
        margin-top: 1.5rem;

        // Room below the link so it can scroll clear of the bottom-left Zendesk chat
        // launcher on phones (layout padding alone leaves ~64px; the launcher needs ~90px).
        @include tablet-and-down {
            padding-bottom: 2.5rem;
        }
    }

    &__wire-alt-title {
        font-size: 0.875rem;
        font-weight: 700;
        color: $black-high;
        margin: 0 0 0.25rem;
    }

    &__wire-alt-desc {
        font-size: 0.8125rem;
        line-height: 1.5;
        color: $black-med;
        margin: 0 0 0.75rem;
    }

    &__wire-link-wrapper {
        display: flex;
        justify-content: center;
        margin-top: 1rem;
    }

    &__wire-link {
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        font-family: inherit;
        font-size: 0.9375rem;
        font-weight: 700;
        color: var(--ch-primary, $blue);
        text-decoration: underline;
        text-decoration-thickness: 2px;
        text-underline-offset: 0.25rem;

        &:hover:not([aria-disabled="true"]) {
            color: var(--ch-primary-dark, $dark-blue);
        }

        &:focus-visible {
            outline: 2px solid var(--ch-primary, $blue);
            outline-offset: 2px;
            border-radius: 0.25rem;
        }

        &[aria-disabled="true"] {
            opacity: 0.6;
            cursor: not-allowed;
        }

        // PAY-3719: secondary-copy treatment for the wire option when digital is available —
        // body-copy color and size with a standard underline, brand color on hover.
        &--subtle {
            font-size: 0.875rem;
            font-weight: 600;
            color: $black-med;
            text-decoration-thickness: 1px;
            text-underline-offset: 0.1875rem;

            // ~44px tap target without changing the visual layout: padding grows the
            // hit area, matching negative margin cancels the added space.
            display: inline-block;
            padding: 0.75rem 0;
            margin: -0.75rem 0;

            &:hover:not([aria-disabled="true"]) {
                color: var(--ch-primary, $blue);
            }
        }
    }

    &__wire-insurance {
        background: $gray-lightest;
        border-radius: 0.375rem;
        padding: 0.625rem 0.75rem;
        font-size: 0.75rem;
        font-weight: 700;
        color: $black-high;
    }

    .payment-methods__card-body :deep(.app-button) {
        margin-top: 1rem;
    }
}
</style>
